/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { isArray } from '@ember/array';
import { get, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { getProgressionJudgementLabel } from 'linced-app/helpers/progression-judgement-label';
import {
  getProgressionForHalfYear,
  mapMilestonesToHalfYears,
  FIRST_HALF,
  SECOND_HALF,
} from 'linced-app/utils/progressions-analysis';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import { byKeys, findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class DotsController extends Controller {
  @service
  session;

  @service
  dates;

  @service
  strings;

  Jurisdiction = Jurisdiction;
  ProgressionJudgement = ProgressionJudgement;
  @tracked sortProp = 'fullName';
  @tracked sortDir = 'asc';

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed(
    'dates.{previousPreviousAcademicYearLabel,previousAcademicYearLabel,currentAcademicYearLabel}',
  )
  get displayYears() {
    return [
      this.dates.previousPreviousAcademicYearLabel,
      this.dates.previousAcademicYearLabel,
      this.dates.currentAcademicYearLabel,
    ];
  }

  @computed('model.{group.name,subject.label}')
  get exportTitle() {
    return `${this.model.group.name} - Progress - ${this.model.subject.label}`;
  }

  @computed('model.subject.milestones.@each.{type,month,year,sequence,label}')
  get halfYearsByMilestone() {
    return mapMilestonesToHalfYears(this.model.subject.milestones);
  }

  @computed('model.{progressions.[],learners.[]}')
  get learnerProgressions() {
    let members = this.get('model.learners') ?? [];
    if (members.length === 0) {
      // No current learners in group - don't bother trying to map anything.
      return {};
    }

    // Build initial empty map of personId -> array.
    let map = members.reduce((map, person) => {
      map[person.id] = [];
      return map;
    }, {});

    // Populate with records.
    let progressions = this.get('model.progressions') ?? [];
    if (progressions.length === 0) {
      // No progression records - just return the empty map.
      return map;
    }

    // Pre-sort progression records in the order we want them to end up.
    progressions = progressions
      .slice()
      .sort((a, b) =>
        a.dueDate !== b.dueDate
          ? a.dueDate > b.dueDate
            ? 1
            : -1
          : a.get('milestone.sequence') > b.get('milestone.sequence')
            ? 1
            : a.get('milestone.sequence') < b.get('milestone.sequence')
              ? -1
              : 0,
      )
      .reverse();
    return progressions.reduce((map, progression) => {
      let personId = progression.person_id;
      // Ignore people who aren't current group members, since we won't show them anyway.
      if (isArray(map[personId])) {
        map[personId].push(progression);
      }
      return map;
    }, map);
  }

  @computed(
    'model.{learners.[],progressions.[]}',
    'learnerProgressions',
    'displayYears.[]',
    'halfYearsByMilestone',
  )
  get dots() {
    return this.model.learners.map((learner) => {
      let progressions = this.get(`learnerProgressions.${learner.id}`) ?? [];
      let years = this.displayYears.map((year) => {
        let yearDates = this.dates.halfyearDatesForAcademicYear(year);
        let firstHalf = getProgressionForHalfYear(
          progressions,
          this.halfYearsByMilestone,
          yearDates,
          FIRST_HALF,
        );
        let secondHalf = getProgressionForHalfYear(
          progressions,
          this.halfYearsByMilestone,
          yearDates,
          SECOND_HALF,
        );
        let judgements = [];
        judgements.push(get(firstHalf || {}, 'judgement') ?? 0);
        judgements.push(get(secondHalf || {}, 'judgement') ?? 0);
        // Rank is [highest judgement][lowest judgement].[first half judgement][second half judgement]
        let rank = 10 * Math.max(...judgements) + Math.min(...judgements);
        rank += judgements[0] / 10;
        rank += judgements[1] / 100;
        rank = rank.toFixed(2);

        return { year: parseInt(year, 10), firstHalf, secondHalf, rank };
      });
      return {
        personId: learner.id,
        years,
      };
    });
  }

  @computed('dots.[]')
  get dotMap() {
    return this.dots.reduce((map, dot) => {
      map[dot.personId] = dot.years;
      return map;
    }, {});
  }

  @computed('sortProp', 'sortDir', 'model.learners.[]')
  get sortedLearners() {
    let sorted = this.get('model.learners');

    // If our sortProp only contains digits and dashes it must be a year (or year range)
    if (this.sortProp.match(/^[\d-]*$/)) {
      // Sorting by a year's dots.  Sort by the rank for the applicable year.
      let unsorted = this.get('model.learners');

      // Build an array of objects which can readily be sorted by the relevant year's rank.
      // Sort it, then map it back to the group membership records which are expected.
      sorted = this.dots
        .map((dots) => {
          return {
            personId: dots.personId,
            rank: findBy(dots.years, 'year', parseInt(this.sortProp, 10)).rank,
          };
        })
        .sort(byKeys('rank'))
        .map((temp) => {
          return unsorted.find((item) => item.id === temp.personId);
        });
    } else {
      // Sorting by a Person property.
      sorted = sorted
        .slice()
        .sort((a, b) =>
          a[this.sortProp] > b[this.sortProp]
            ? 1
            : a[this.sortProp] < b[this.sortProp]
              ? -1
              : 0,
        );
    }

    // Reverse the array if descending sort was requested.
    if (this.sortDir === 'desc') {
      sorted = sorted.reverse();
    }
    return sorted;
  }

  @computed('sortedLearners.[]', 'dots.[]', 'jurisdiction')
  get exportData() {
    return this.sortedLearners.map((learner) => {
      let person = learner.getProperties([
        'id',
        'jurisdictionalId',
        'fullName',
        'gender',
        'displayEthnicity',
        'firstSchooling',
        'currentYearLevel',
      ]);
      let row = [
        person.jurisdictionalId,
        person.fullName,
        person.gender,
        person.displayEthnicity,
        person.firstSchooling,
        person.currentYearLevel,
      ];
      let years = this.get(`dotMap.${person.id}`) ?? [];
      years.forEach((year) => {
        if (year.firstHalf) {
          row.push(
            getProgressionJudgementLabel(
              year.firstHalf.judgement,
              { capitalize: true },
              this.strings,
            ),
          );
        } else {
          row.push('');
        }
        if (year.secondHalf) {
          row.push(
            getProgressionJudgementLabel(
              year.secondHalf.judgement,
              { capitalize: true },
              this.strings,
            ),
          );
        } else {
          row.push('');
        }
      });
      return row;
    });
  }

  @tracked
  selected = [];

  @action
  changeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @action
  toggleSelected(person) {
    if (this.selected.includes(person)) {
      this.selected = this.selected.filter((item) => item !== person);
    } else {
      this.selected = [...this.selected, person];
    }
  }

  @action
  toggleAll() {
    if (this.selected.length === this.get('model.learners.length')) {
      // All already selected; clear selection.
      this.selected = [];
    } else {
      // Not all selected; select them all.
      let selected = this.selected;
      this.get('model.learners').forEach((learner) => {
        if (!selected.includes(learner)) {
          selected = [...selected, learner];
        }
      });
      this.selected = selected;
    }
  }
}
